import React, {
  RefObject,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import "../common/Insights.scss";
import { useIntl } from "react-intl";

interface BarChartProps {
  amount: number;
  actualCount: number;
  expectedCount: number;
  tooltipCompletedText: string;
  tooltipNotCompletedText: string;
}
interface BarChartState {
  tooltipVisible: boolean;
  tooltipPosition: { x: number; y: number };
  tooltipTitle: string;
  tooltipText: string;
}
const initialState: BarChartState = {
  tooltipVisible: false,
  tooltipPosition: { x: 0, y: 0 },
  tooltipTitle: "",
  tooltipText: "",
};

interface BarChartAction {
  type: string;
  payload: { x: number; y: number };
  title: string;
  label: string;
}
const defaultAction = {
  type: "",
  payload: { x: 0, y: 0 },
  title: "",
  label: "",
};

const reducer = (state: BarChartState, action: BarChartAction) => {
  switch (action.type) {
    case "showTooltip":
      return {
        ...state,
        tooltipVisible: true,
        tooltipPosition: action.payload,
        tooltipTitle: action.title,
        tooltipText: action.label,
      };
    case "hideTooltip":
      return {
        ...state,
        tooltipVisible: false
      };
    default:
      return state;
  }
};
const AssessmentProgressChart = ({
  amount,
  actualCount,
  expectedCount,
  tooltipCompletedText,
  tooltipNotCompletedText,
}: BarChartProps) => {
  const greenBarWidth = `${amount}%`;
  const grayBarWidth = `${100 - amount}%`;
  const [state, dispatch] = useReducer(reducer, initialState);
  let notCompletedCount = expectedCount - actualCount;

  const showTooltip = (
    event: React.MouseEvent<HTMLDivElement>,
    count: number,
    totalCount: number,
    completedText: string,
    isGreenBar?: boolean
  ) => {
    const { offsetX, offsetY } = event.nativeEvent;
    const barContainerWidth = event.currentTarget.parentElement?.clientWidth;
    let greenBarWidth = 0;
    if (barContainerWidth) {
      greenBarWidth = (amount / 100) * barContainerWidth;
    }
    let correctedOffsetX = isGreenBar ? offsetX : offsetX + greenBarWidth;
    if (event.type !== "click") {
      correctedOffsetX = isGreenBar ? offsetX : greenBarWidth;
    }
    dispatch({
      type: "showTooltip",
      payload: { x: correctedOffsetX, y: offsetY },
      title: `${count}/${totalCount}`,
      label: completedText,
    });
  };

  const handleArrowKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === "ArrowUp" ||
      event.key === "ArrowDown" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      event.preventDefault();
      if (
        !state.tooltipVisible ||
        state.tooltipText === tooltipNotCompletedText
      ) {
        (
          event.currentTarget.querySelector(".green-bar") as HTMLElement
        )?.focus();
        showTooltip(
          event,
          actualCount,
          expectedCount,
          tooltipCompletedText,
          true
        );
      } else if (
        state.tooltipVisible ||
        state.tooltipText === tooltipCompletedText
      ) {
        (
          event.currentTarget.querySelector(".gray-bar") as HTMLElement
        )?.focus();
        showTooltip(
          event,
          notCompletedCount,
          expectedCount,
          tooltipNotCompletedText,
          false
        );
      }
    } else if (event.key === "Tab") {
      dispatch({ ...defaultAction, type: "hideTooltip" });
      (
        event.currentTarget.parentElement.querySelector(
          ".bar-container"
        ) as HTMLElement
      )?.focus();
    }
  };

  const tooltipRef: RefObject<HTMLDivElement> = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        dispatch({ ...defaultAction, type: "hideTooltip" });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const ariaLabel = useIntl().formatMessage({
    id: "app.assessmentProgressChart.ariaLabel",
  });
  return (
    <>
      <div className="bar-chart flex-row">
        <div
          className="bar-container"
          tabIndex={0}
          aria-label={ariaLabel}
          role="application"
          onKeyDown={handleArrowKeyPress}
        >
          <div
            className="green-bar"
            style={{ width: `calc(${greenBarWidth} - 1px)` }}
            tabIndex={-1}
            onBlur={() => dispatch({ ...defaultAction, type: "hideTooltip" })}
            onClick={(event) => {
              showTooltip(
                event,
                actualCount,
                expectedCount,
                tooltipCompletedText,
                true
              );
            }}
          ></div>

          <div className={amount === 0 ? "" : "gap"}></div>
          <div
            className="gray-bar"
            style={{ width: `calc(${grayBarWidth} - 1px)` }}
            tabIndex={-1}
            onBlur={() => dispatch({ ...defaultAction, type: "hideTooltip" })}
            onClick={(event) => {
              showTooltip(
                event,
                notCompletedCount,
                expectedCount,
                tooltipNotCompletedText,
                false
              );
            }}
          ></div>
        </div>
        <div className="percentage-text-label">{Math.round(amount)}%</div>
      </div>

      {state.tooltipVisible && (
        <Tooltip
          tooltipTitle={state.tooltipTitle}
          tooltipText={state.tooltipText}
          tooltipRef={tooltipRef}
          tooltipPosition={state.tooltipPosition}
        />
      )}
    </>
  );
};

interface tooltipProps {
  tooltipTitle: string;
  tooltipText: string;
  tooltipRef: any;
  tooltipPosition: { x: number; y: number };
}
function Tooltip({
  tooltipTitle,
  tooltipText,
  tooltipRef,
  tooltipPosition,
}: tooltipProps) {
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [moveRight, setMoveRight] = useState(false);

  useEffect(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.innerText = `${tooltipTitle} : ${tooltipText}`;
    }
  }, [tooltipTitle, tooltipText]);

  // Check if tooltip needs to be flipped vertically or moved horizontally
  useEffect(() => {
    if (tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const popoverEl = tooltipRef.current.querySelector(
        ".popover"
      ) as HTMLElement;

      if (popoverEl) {
        const popoverRect = popoverEl.getBoundingClientRect();

        // Check if the right edge of the tooltip extends beyond the browser viewport
        const isCutOffRight = popoverRect.right > window.innerWidth;
        setMoveRight(isCutOffRight);

        // Check if the tooltip is cut off at the top (above viewport)
        const isCutOffAbove = popoverRect.top < 0;

        if (isCutOffAbove) {
          // Check if there's enough room below
          const spaceBelow = window.innerHeight - tooltipRect.bottom;
          const tooltipHeight = popoverRect.height;

          if (spaceBelow >= tooltipHeight) {
            setIsFlipped(true);
          } else {
            setIsFlipped(false);
          }
        } else {
          setIsFlipped(false);
        }
      }
    }
  }, [tooltipPosition, tooltipRef]);

  return (
    <div
      className={`tooltip ${isFlipped ? "flip-vertical" : ""} ${
        moveRight ? "move-right" : ""
      }`}
      ref={tooltipRef}
      style={{
        left: tooltipPosition.x,
        top: tooltipPosition.y,
      }}
    >
      <div
        className="sr-only"
        aria-atomic={true}
        aria-live="polite"
        role="alert"
        ref={liveRegionRef}
      ></div>
      <div className={`popover`}>
        <div className="tooltip-text">
          <div className={`popover-amount`}>{tooltipTitle}</div>
          <div className="popover-text">{tooltipText}</div>
        </div>
      </div>
    </div>
  );
}

export default AssessmentProgressChart;
